/**
 * THE token source. One file, three generated artefacts (ADR 0008):
 *
 *   1. `./index.ts`        — the typed TS object, for runtime values
 *   2. `./css.ts`          — CSS custom properties  -> src/styles/tokens.css
 *   3. `./tailwind.ts`     — the Tailwind theme      -> tailwind-preset.cjs
 *
 * A drift test asserts (2) and (3) on disk still equal what this file produces.
 *
 * ── The two-layer model (doc 40 §2 / §3) ─────────────────────────────────────
 *
 * Layer 1 is the **primitive palettes**: `sand`, `rose`, `green`, `orange`,
 * `red`, `blue`, `yellow`, `cyan`. They say what a colour *is*.
 *
 * Layer 2 is the **semantic roles**: `text-*`, `bg-*`, `border-*`, plus the
 * shadow, escalation and gradient roles. They say what a colour *means*.
 *
 * (H) doc 40 §2/§3: application code consumes layer 2 only. A primitive in a
 * component means the component has decided what something *is* rather than
 * what it *means*, and the `noPrimitiveTokens` ESLint rule in `@sarva/config`
 * makes that an error rather than a review finding people forget to make.
 *
 * ── Dark theme ───────────────────────────────────────────────────────────────
 *
 * (H) doc 40 §15: deferred, deliberately. Every role below is indirected
 * through a primitive, so a second theme is a second role map in this file —
 * not a refactor. Nothing outside this token layer may assume a light ground.
 */

/* ══════════════════════════════════════════════════════════════════════════ *
 * LAYER 1 — PRIMITIVE PALETTES (doc 40 §2)
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * The neutral spine — Tailwind's cool grey ramp, shared with the Voice Agent and
 * Policy Intelligence so the Sarva products read as one suite (doc 40 §16, v3).
 * Steps follow Tailwind's 50–950 names, so a value can be checked against the
 * reference app's stylesheet by eye.
 */
export const gray = {
  50: "#F9FAFB",
  100: "#F3F4F6",
  200: "#E5E7EB",
  300: "#D1D5DB",
  400: "#9CA3AF",
  /** Not a Tailwind step: the lightest cool grey that clears 3:1 on white
   * (3.30:1; 3.15:1 on the canvas) — the control edge. WCAG 1.4.11 needs it; gray-400 is 2.53. */
  450: "#878E99",
  500: "#6B7280",
  600: "#4B5563",
  700: "#374151",
  800: "#1F2937",
  900: "#111827",
  950: "#030712",
} as const;

/**
 * The brand ramp — Sarva fuchsia, the Voice Agent's accent. `fuchsia-600` carries
 * white text at 4.71:1; `fuchsia-700` is the text/link tone (6.32:1).
 * (H) 50–300 are fills and decoration and are never used as text.
 */
export const fuchsia = {
  50: "#FDF4FF",
  100: "#FAE8FF",
  200: "#F5D0FE",
  300: "#F0ABFC",
  400: "#E879F9",
  500: "#D946EF",
  /** THE brand colour. */
  600: "#C026D3",
  700: "#A21CAF",
  800: "#86198F",
  900: "#701A75",
  950: "#4A044E",
} as const;

/** Gradient partners only (sign-in panel, brand mark). */
export const purple = { 50: "#FAF5FF", 500: "#A855F7", 600: "#9333EA", 700: "#7E22CE" } as const;
export const pink = { 600: "#DB2777", 700: "#BE185D" } as const;

export const green = { 50: "#F0FDF4", 100: "#DCFCE7", 600: "#16A34A", 700: "#15803D", 800: "#166534" } as const;
export const amber = { 50: "#FFFBEB", 100: "#FEF3C7", 600: "#D97706", 700: "#B45309" } as const;
export const orange = { 50: "#FFF7ED", 600: "#EA580C", 700: "#C2410C" } as const;
export const red = { 50: "#FEF2F2", 100: "#FEE2E2", 600: "#DC2626", 700: "#B91C1C", 800: "#991B1B" } as const;
export const blue = { 50: "#EFF6FF", 600: "#2563EB", 700: "#1D4ED8" } as const;
export const yellow = { 50: "#FEFCE8", 700: "#A16207", 800: "#854D0E" } as const;

/** Not a palette step: the one absolute. `bg-default` and `text-on-primary`. */
export const white = "#FFFFFF";

export const palettes = { gray, fuchsia, purple, pink, green, amber, orange, red, blue, yellow } as const;
export type PaletteName = keyof typeof palettes;

/* ══════════════════════════════════════════════════════════════════════════ *
 * LAYER 2 — SEMANTIC ROLES (doc 40 §3)
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Text roles. Ratios in the comments are against `bg-default` (white) and are
 * asserted by the contrast test, which is the authority (doc 40 §14).
 * Three working tiers — default / secondary / tertiary — and nothing else greys text.
 */
export const textRoles = {
  /** 17.74:1 — body, headings, money figures */
  default: gray[900],
  /** 7.56:1 — labels, column values that support the primary one */
  secondary: gray[600],
  /** 4.83:1 — captions, timestamps, table headers, helper text */
  tertiary: gray[500],
  /** 2.53:1 — WCAG 1.4.3 exempt (disabled control labels) */
  disabled: gray[400],
  /** 3.30:1 — input placeholders. A hint, never the accessible name. */
  placeholder: gray[450],
  /** 6.32:1 — links and brand-coloured text */
  primary: fuchsia[700],
  /** 5.02:1 */
  success: green[700],
  /** 5.02:1 */
  warning: amber[700],
  /** 6.47:1 */
  danger: red[700],
  /** 6.70:1 */
  info: blue[700],
  /** 4.71:1 on `bg-primary` */
  "on-primary": white,
  /** 17.74:1 on `bg-inverted` — tooltips. */
  "on-inverted": white,
  "on-danger": white,
  "on-success": white,
} as const;

/** Background roles. */
export const bgRoles = {
  /** Cards, tables, panels — the working surface */
  default: white,
  /** The page ground behind cards */
  canvas: gray[50],
  /** Row and control hover */
  hover: gray[50],
  /** Pressed, selected row, segmented-control track */
  active: gray[100],
  /** Wells, disabled fills, track backgrounds, table header band */
  subtle: gray[100],
  /** Tooltips. */
  inverted: gray[900],

  primary: fuchsia[600],
  "primary-hover": fuchsia[700],
  "primary-active": fuchsia[800],
  /** Active nav item, brand status pill */
  "primary-weak": fuchsia[50],

  success: green[700],
  "success-weak": green[50],
  warning: amber[700],
  "warning-weak": amber[50],
  danger: red[700],
  "danger-weak": red[50],
  info: blue[700],
  "info-weak": blue[50],

  /** The scrim behind a modal or drawer. Dark enough that a figure behind an
   * unsubmitted form does not read as live. */
  scrim: "rgba(17, 24, 39, 0.5)",
} as const;

/**
 * Border roles.
 *
 * (H) doc 40 §3: a card edge is a divider (`default`, 1.23:1 — the shadow does
 * the separating) and is wrong for a control edge. WCAG needs 3:1 for the
 * boundary of something operated, so inputs, selects and checkboxes use
 * `control` (3.30:1).
 */
export const borderRoles = {
  /** 1.10:1 — row dividers inside a card. */
  weak: gray[100],
  /** 1.23:1 — card and panel edges, table frame. */
  default: gray[200],
  /** 3.30:1 — the edge of an input, select, checkbox: anything operated. */
  control: gray[450],
  /** 4.83:1 — emphasis, focused control edge. */
  strong: gray[500],
  /** 4.71:1 — brand-accented edge, selected state. */
  primary: fuchsia[600],

  success: green[600],
  warning: amber[600],
  danger: red[600],
  info: blue[600],
} as const;

/**
 * Status tones (doc 40 §4): a chip is the tone's 700 text on its 50 fill — the
 * Voice Agent's treatment. A dot, where one is drawn, is the 600 step (≥3:1).
 * (H) colour is never the only signal: a chip always carries its label.
 *
 * Which tone a status has comes from the generated `*_STATE_META` in
 * `@sarva/domain-types` (doc 27 §3).
 */
export const toneRoles = {
  neutral: { dot: gray[500], text: gray[600], weak: gray[100], border: gray[300] },
  info: { dot: blue[600], text: blue[700], weak: blue[50], border: blue[600] },
  success: { dot: green[600], text: green[700], weak: green[50], border: green[600] },
  warning: { dot: amber[600], text: amber[700], weak: amber[50], border: amber[600] },
  danger: { dot: red[600], text: red[700], weak: red[50], border: red[600] },
  /**
   * A realised loss. (H) doc 40 §5: the only solid-filled treatment in the
   * system, so it differs from an urgent warning by fill and not only hue.
   */
  loss: { dot: white, text: white, weak: red[700], border: red[700] },
} as const;

export type Tone = keyof typeof toneRoles;

/**
 * The escalation scale (doc 40 §5). Deadline proximity is this product's
 * central concept; it maps onto the semantic families rather than inventing a
 * parallel palette, so it reads consistently with every other status.
 *
 * `row` is how a queue row carries the band: a left border or a tint, never a
 * weight change.
 */
export const escalation = {
  0: {
    key: "comfortable",
    label: "Comfortable",
    minPct: 50,
    dot: gray[500],
    text: gray[600],
    surface: "transparent",
    border: "transparent",
    row: "none",
    solid: false,
  },
  1: {
    key: "working",
    label: "Working",
    minPct: 25,
    dot: blue[600],
    text: blue[700],
    surface: blue[50],
    border: blue[600],
    row: "none",
    solid: false,
  },
  2: {
    key: "attention",
    label: "Attention",
    minPct: 10,
    dot: yellow[700],
    text: yellow[800],
    surface: yellow[50],
    border: yellow[700],
    row: "none",
    solid: false,
  },
  3: {
    key: "urgent",
    label: "Urgent",
    minPct: 5,
    dot: orange[600],
    text: orange[700],
    surface: orange[50],
    border: orange[600],
    row: "left-border",
    solid: false,
  },
  4: {
    key: "critical",
    label: "Critical",
    minPct: 0,
    dot: red[600],
    text: red[700],
    surface: red[50],
    border: red[600],
    row: "left-border",
    solid: false,
  },
  5: {
    key: "expired",
    label: "Expired",
    minPct: -1,
    dot: white,
    text: white,
    surface: red[700],
    border: red[700],
    row: "left-border",
    /** (H) doc 40 §5: band 5 is the only solid-filled chip in the system. */
    solid: true,
  },
} as const;

export type EscalationBand = keyof typeof escalation & number;

/**
 * Chart colours (doc 40 §12a). Charts draw from these and nothing else — no
 * library default blue. Series colours carry meaning (money in / at risk / lost),
 * not identity, and every chart ships a legend and a table view.
 */
export const chartRoles = {
  primary: fuchsia[600],
  secondary: purple[500],
  positive: green[600],
  attention: amber[600],
  negative: red[600],
  neutral: gray[400],
  grid: gray[100],
  axis: gray[200],
} as const;

/** Ordered high-to-low, for `escalationBandFor`. Band 5 is time-based, not a
 * percentage, so it is not in this table. */
export const escalationBands = [
  { band: 0, minPct: 50 },
  { band: 1, minPct: 25 },
  { band: 2, minPct: 10 },
  { band: 3, minPct: 5 },
] as const;

/* ══════════════════════════════════════════════════════════════════════════ *
 * ELEVATION (doc 40 §6)
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * Depth comes from borders. Shadows are neutral, subtle and named by role —
 * (H) never brand-tinted and never a glow (doc 40 §16: a coloured shadow
 * behind a data grid reads as a smudge).
 *
 * (H) A table row, a list item and an inline panel are FLAT: border only.
 * There is deliberately no `shadow-row`.
 */
export const shadowRoles = {
  /** Cards and panels: a hairline lift, so the light border can stay light. */
  card: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
  "button-hover": "0 1px 3px 0 rgba(16, 24, 40, 0.10)",
  menu: "0 4px 16px 0 rgba(16, 24, 40, 0.08)",
  tooltip: "0 4px 12px 0 rgba(16, 24, 40, 0.15)",
  drawer: "0 16px 48px 0 rgba(16, 24, 40, 0.18)",
  modal: "0 16px 48px 0 rgba(16, 24, 40, 0.18)",
  sidebar: "none",
  "focus-ring": "0 0 0 3px rgba(192, 38, 211, 0.25)",
} as const

/* ══════════════════════════════════════════════════════════════════════════ *
 * SHAPE (doc 40 §7)
 * ══════════════════════════════════════════════════════════════════════════ */

export const radius = {
  none: "0px",
  /** Small indicators, progress tracks */
  xs: "3px",
  /** (H) Buttons, inputs, menus — the most used value */
  sm: "7px",
  /** Chips inside a card, nested wells */
  md: "8px",
  /** Cards, panels, tables */
  lg: "10px",
  /** Modals, drawers */
  xl: "14px",
  /** Pills, badges, avatars, status dots */
  round: "999px",
} as const

/* ══════════════════════════════════════════════════════════════════════════ *
 * SPACING (doc 40 §9)
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * The 8px grid, enumerated explicitly rather than inherited, plus the three
 * additions doc 40 §9 names: `4.5 = 18px`, `13 = 52px`, `14 = 56px`.
 *
 * Enumerating it REPLACES Tailwind's scale, which is the point: (H) doc 40 §9
 * bans arbitrary pixel values in application code, and the cheapest way to
 * enforce that is to make `p-[13px]`'s named cousins simply not exist.
 */
export const spacing = {
  0: "0px",
  px: "1px",
  0.5: "2px",
  1: "4px",
  1.5: "6px",
  2: "8px",
  2.5: "10px",
  3: "12px",
  3.5: "14px",
  4: "16px",
  /** doc 40 §9 addition */
  4.5: "18px",
  5: "20px",
  6: "24px",
  7: "28px",
  8: "32px",
  9: "36px",
  10: "40px",
  11: "44px",
  12: "48px",
  /** doc 40 §9 addition */
  13: "52px",
  /** doc 40 §9 addition — the top nav height */
  14: "56px",
  16: "64px",
  20: "80px",
  24: "96px",
  28: "112px",
  32: "128px",
  40: "160px",
  48: "192px",
  56: "224px",
  64: "256px",
  80: "320px",
} as const;

/* ══════════════════════════════════════════════════════════════════════════ *
 * LAYOUT — THE APPLICATION SHELL (doc 40 §8)
 * ══════════════════════════════════════════════════════════════════════════ */

export const layout = {
  topnavHeight: "56px",
  sidebarWidth: "216px",
  sidebarCollapsedWidth: "48px",
  contentMaxWidth: "1600px",
  contentGutter: "24px",
  /** doc 41 §8: long-form measure. */
  proseMaxWidth: "68ch",
} as const;

/**
 * (H) doc 40 §8: named, never an ad-hoc number.
 *
 * The five values doc 40 §8 fixes are `sidebar 30 · topnav 40 · drawer 50 ·
 * modal 60 · tooltip 70`, kept exactly. `menu` and `toast` are additions, and
 * the reason is concrete rather than tidiness:
 *
 *  - `menu` (65) must sit ABOVE `modal` (60), because a `<Select>` or a column
 *    menu opened inside a dialog portals to `<body>` as a sibling of the
 *    dialog. At 55 it would render behind the dialog it belongs to — a bug the
 *    documented scale has no room to express.
 *  - `toast` (80) must sit above everything, because a toast reporting that a
 *    payment failed is worthless if the dialog that triggered it covers it.
 *
 * Flagged in the handover as a deviation from doc 40 §8.
 */
export const zIndex = {
  base: "0",
  sidebar: "30",
  topnav: "40",
  drawer: "50",
  modal: "60",
  /** Addition: menus, popovers, selects — above a modal, see above. */
  menu: "65",
  tooltip: "70",
  /** Addition: above everything. */
  toast: "80",
} as const;

/* ══════════════════════════════════════════════════════════════════════════ *
 * TYPE (doc 41)
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * (H) doc 41 §1: both faces are self-hosted, latin-subset, variable `woff2`,
 * `font-display: swap`, preloaded from our own origin. A font request from a
 * PHI-bearing application leaks the referring URL to a third party. A build
 * that cannot find the files fails loudly and NEVER falls back to a CDN link.
 */
export const fontFamily = {
  /** The platform UI face (SF Pro on macOS, Segoe UI on Windows). Nothing is
   * downloaded, so there is no third-party request and no font-swap flash — the
   * privacy rule doc 41 §1 exists for is met by construction. */
  sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  /** Retained for printed letterhead only. Never on a working screen. */
  display: '"Domine Variable", Domine, Georgia, serif',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
} as const

/**
 * (H) doc 41 §3, applied globally.
 *
 *   tnum — digits align vertically down a currency column. The single
 *          highest-value typographic decision in the product.
 *   zero — a slashed zero, so `0` cannot be misread as `O` in a member id,
 *          a claim number or a payer id. A misread id is a rejected claim.
 *   cv05 — Inter's straight-tailed `l`, keeping `1 / l / I` distinct.
 *
 * This value is fed into Tailwind's `fontFamily.sans` options object as well
 * as into `base.css`, and the Tailwind route is the load-bearing one:
 * preflight emits `html { font-feature-settings: theme(fontFamily.sans[1]
 * .fontFeatureSettings, normal) }` and `@tailwind base` is imported AFTER
 * base.css, so without this the preflight default of `normal` silently wins
 * and the whole document loses tabular numerals. Verified in a browser.
 */
export const FONT_FEATURE_SETTINGS = "normal";

/**
 * (H) doc 41 §3, applied to NUMBERS only: tabular digits so a currency column
 * aligns, and a slashed zero so `0` cannot be read as `O` in an id. Applied by
 * `<Money>`, `<DateTime>`, `<BusinessDate>`, `.identifier`, numeric table cells
 * and stat figures. Never on `html`: tabular forms give hyphens and punctuation
 * digit width, which is what spaced out "Month‑end" and "CLM‑DEMO‑7" in v2.
 */
export const NUMERIC_FEATURE_SETTINGS = '"tnum" 1, "zero" 1';

/**
 * doc 41 §2. Names follow the reference system so the two codebases read alike.
 *
 * (H) `sm` (14px) is the body size, not `base`. The screens users live in are
 * dense lists; 16px costs visible rows on a laptop for no legibility gain.
 * (H) Nothing below 11px, at any density — `2xs` is the floor.
 */
export const typeScale = {
  /** Page titles. */
  "3xl": { size: "26px", lineHeight: "32px", weight: 600, tracking: "-0.01em" },
  /** Stat figures, record headline numbers. */
  "2xl": { size: "22px", lineHeight: "28px", weight: 700, tracking: "-0.01em" },
  /** Dialog titles, record identity line. */
  xl: { size: "18px", lineHeight: "24px", weight: 600, tracking: "-0.01em" },
  /** Panel and section titles. */
  lg: { size: "15px", lineHeight: "22px", weight: 600, tracking: "-0.005em" },
  /** Emphasised body, detail-view values. */
  base: { size: "14px", lineHeight: "20px", weight: 400, tracking: "0" },
  /** (H) The default body size. Table cells, form values, most text. */
  sm: { size: "13.5px", lineHeight: "20px", weight: 400, tracking: "0" },
  /** Labels, captions, metadata, chip text. */
  xs: { size: "12.5px", lineHeight: "18px", weight: 400, tracking: "0" },
  /** The floor. Table headers (uppercase), overlines. */
  "2xs": { size: "11.5px", lineHeight: "16px", weight: 600, tracking: "0.04em" },
} as const

/** doc 41 §1: 400 body · 500 emphasis/headers/nav · 600 headings/buttons/stat
 * figures · 700 page titles and display. */
export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

/** (H) doc 41 §3: identifiers use tabular numerals and this tracking, for
 * character discrimination in long member-id / claim-number strings. */
export const IDENTIFIER_LETTER_SPACING = "0.01em";

/* ══════════════════════════════════════════════════════════════════════════ *
 * DENSITY (doc 40 §10) AND MOTION (doc 40 §11)
 * ══════════════════════════════════════════════════════════════════════════ */

export const density = {
  comfortable: { rowHeight: "48px", controlHeight: "36px", cellPaddingY: "14px", bodySize: "13.5px" },
  /** (H) The default for queues and lists. */
  compact: { rowHeight: "44px", controlHeight: "34px", cellPaddingY: "12px", bodySize: "13.5px" },
  /**
   * `cellPaddingY: "4px"`, not 6px, and the arithmetic is the reason.
   *
   * doc 40 §10 puts a `dense` row at 32px. doc 40's target-size rule keeps every hit target
   * at ≥24×24px, and the chips a row carries (`StatusBadge`, `Pill`) are exactly 24px. At
   * 6px of vertical cell padding that row is 24 + 12 = **36px**, so the documented 32px was
   * unreachable — measured on `/denials`, where dense rows came out at 37px and the chip
   * was the floor. Two rules in the same document that could not both hold.
   *
   * 4px reconciles them exactly: 24 + 8 = 32. The chip keeps its target and the row hits its
   * documented height. See docs/30-design/42_DESIGN_AUDIT.md, D10.
   */
  dense: { rowHeight: "32px", controlHeight: "28px", cellPaddingY: "4px", bodySize: "12.5px" },
} as const;

export type DensityMode = keyof typeof density;

/** (H) doc 40 §10: hit targets stay >=24x24px in every density mode. */
export const MIN_TARGET_PX = 24;

/**
 * Functional motion only, taken from the reference system.
 *
 * (H) doc 40 §11: no animation on data as it loads, and no decorative
 * animation anywhere — a user works several hundred items a session and pays
 * for each flourish every time. `prefers-reduced-motion` disables all of it.
 */
export const motion = {
  "fade-in": { duration: "150ms", easing: "ease-out" },
  "fade-out": { duration: "150ms", easing: "ease" },
  "scale-in": { duration: "150ms", easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  "drawer-in": { duration: "250ms", easing: "cubic-bezier(0.16, 1, 0.3, 1)" },
  "drawer-out": { duration: "200ms", easing: "ease-in" },
  hover: { duration: "120ms", easing: "ease-out" },
} as const;

/* ══════════════════════════════════════════════════════════════════════════ *
 * GRADIENTS (doc 40 §12)
 * ══════════════════════════════════════════════════════════════════════════ */

/**
 * (H) doc 40 §12: marketing surfaces, sign-in and empty states only. Never
 * behind data, a table, a form, or any surface showing money or PHI.
 */
export const gradients = {
  primary: `linear-gradient(to right, ${fuchsia[600]}, ${pink[600]})`,
  "primary-hover": `linear-gradient(to right, ${fuchsia[700]}, ${pink[700]})`,
  /** The sign-in panel. White text on it clears 4.5:1 at every stop. */
  hero: `linear-gradient(to bottom right, ${fuchsia[600]}, ${purple[600]}, ${pink[600]})`,
  weak: `linear-gradient(to right, ${fuchsia[50]}, ${white})`,
} as const

/* ══════════════════════════════════════════════════════════════════════════ *
 * ACCESSIBILITY FLOORS (doc 40 §14)
 * ══════════════════════════════════════════════════════════════════════════ */

export const CONTRAST_FLOOR = {
  /** Body text */
  bodyText: 4.5,
  /** Large text: >=24px, or >=19px bold */
  largeText: 3,
  /** UI components, boundaries, status dots */
  uiComponent: 3,
} as const;

/**
 * (H) doc 40 §14: the known and deliberate exemptions, listed here so the
 * contrast test can assert them **as exemptions**. An exemption that is merely
 * "not tested" is indistinguishable from an accident; an exemption with an
 * asserted ratio and a reason cannot silently drift.
 *
 * doc 40 §14 requires each to be asserted from *both* sides — below the floor
 * it is exempt from, and above the floor it still meets — so a palette edit
 * that "fixes" one fails the test and forces the exemption to be removed
 * deliberately rather than silently.
 *
 * `maxRatio` is the ceiling the value must stay under. If somebody darkens
 * `border-weak` into a control-grade border the test fails and the exemption
 * gets deleted on purpose, rather than lingering as a lie.
 */
export const CONTRAST_EXEMPTIONS = [
  {
    token: "border-weak",
    foreground: borderRoles.weak,
    background: bgRoles.default,
    ratio: 1.1,
    maxRatio: 3,
    reason:
      "A divider between rows, not the boundary of anything operated. WCAG 1.4.11 " +
      "applies to the boundary of a component a user operates; a row separator is " +
      "decoration. ",
  },
  {
    token: "border-default",
    foreground: borderRoles.default,
    background: bgRoles.default,
    ratio: 1.23,
    maxRatio: 3,
    reason:
      "Card and panel edge — a divider, not a control. Anything a user operates uses " +
      "border-control (3.30:1) instead. Keeping the two apart by name is doc 40 §3.",
  },
  {
    token: "text-disabled",
    foreground: textRoles.disabled,
    background: bgRoles.default,
    ratio: 2.53,
    maxRatio: 4.5,
    reason:
      "WCAG 1.4.3 explicitly exempts text in an inactive user-interface component. " +
      "A disabled control in this product is always accompanied by an explanation " +
      "rendered in text-secondary (7.56:1), so the reason is never disabled-grey.",
  },
  {
    token: "text-placeholder",
    foreground: textRoles.placeholder,
    background: bgRoles.default,
    ratio: 3.3,
    maxRatio: 4.5,
    reason:
      "Clears the 3:1 non-text floor, and <Field> always renders a real <label> — so " +
      "nothing in the product exists only as placeholder grey. (H) THE EXEMPTION IS " +
      "VOID the moment a placeholder becomes the only label (doc 40 §14). That " +
      "condition is not prose: `Field.test.tsx` asserts a real <label> is rendered " +
      "for every field, so removing it fails the suite rather than quietly " +
      "invalidating this row.",
  },
] as const;

/** Steps that are fills and decoration and (H) never text (doc 40 §14). */
export const NON_TEXT_BRAND_STEPS = [50, 100, 200, 300] as const;
